import Point from '@arcgis/core/geometry/Point';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { identify } from '@arcgis/core/rest/identify';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import Query from '@arcgis/core/rest/support/Query';
import type {
  QueryFeatureLayerParams,
  IdentifyMapServiceParams,
  HelperFunctionResult,
} from './types';

/**
 * Queries features from an ArcGIS feature layer at a specified geographic point.
 *
 * This function creates a spatial query to find features that intersect with the given
 * longitude/latitude coordinates. It extracts feature attributes and metadata,
 * providing a consistent response format for click-to-identify operations.
 *
 * @param {object} layer - The ArcGIS feature layer to query (must have queryFeatures method)
 * @param {number} longitude - The longitude coordinate for the query point
 * @param {number} latitude - The latitude coordinate for the query point
 * @param {string} [fallbackLayerName] - Optional fallback name if layer title cannot be determined
 *
 * @returns {Promise<HelperFunctionResult | null>} Promise that resolves to:
 *   - Object with sourceTitle and attributes if features found
 *   - null if no features found or query fails
 *
 * @example
 * const result = await queryFeatureLayer({
 *   layer: esriFeatureLayer,
 *   longitude: -122.4194,
 *   latitude: 37.7749,
 *   fallbackLayerName: 'San Francisco Parcels'
 * });
 */
export const queryFeatureLayer = async ({
  layer,
  longitude,
  latitude,
  fallbackLayerName,
  bufferMeters,
}: QueryFeatureLayerParams): Promise<HelperFunctionResult | null> => {
  if (
    !layer ||
    typeof layer !== 'object' ||
    !('queryFeatures' in layer) ||
    typeof layer.queryFeatures !== 'function'
  ) {
    return null;
  }

  try {
    const queryOptions: __esri.QueryProperties = {
      geometry: new Point({
        longitude,
        latitude,
        spatialReference: { wkid: 4326 },
      }),
      outFields: ['*'],
    };

    if (typeof bufferMeters === 'number' && bufferMeters > 0) {
      queryOptions.distance = bufferMeters;
      queryOptions.units = 'meters';
      queryOptions.spatialRelationship = 'intersects';
    }

    const query = new Query(queryOptions);

    const result = await layer.queryFeatures(query);
    if (result?.features?.length) {
      const primaryResult = result.features[0];
      const nameLayer = (primaryResult.layer || layer) as FeatureLayer;
      const sourceTitle =
        nameLayer?.sourceJSON?.name ||
        nameLayer?.title ||
        fallbackLayerName ||
        'Unknown Layer';

      return {
        sourceTitle,
        attributes: {
          ...result.features[0].attributes,
          _sourceLayer: sourceTitle,
        },
      };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Feature layer query failed:', error);
  }

  return null;
};

/**
 * Identifies features from an ArcGIS map service at a specified geographic point.
 *
 * This function performs an identify operation on map image services using the ArcGIS
 * REST API. It handles authentication via tokens and provides a consistent response
 * format for click-to-identify operations on map services (as opposed to feature services).
 *
 * @param {string} url - The map service URL to query
 * @param {number} longitude - The longitude coordinate for the identify point
 * @param {number} latitude - The latitude coordinate for the identify point
 * @param {object} view - The map view object containing width, height, and extent information
 * @param {string} [token] - Optional authentication token for secured services
 * @param {string} [fallbackLayerName] - Optional fallback name if layer name cannot be determined
 *
 * @returns {Promise<HelperFunctionResult | null>} Promise that resolves to:
 *   - Object with sourceTitle and attributes if features identified
 *   - null if no features found or identify operation fails
 *
 * @example
 * const result = await identifyMapService({
 *   url: 'https://services.arcgis.com/sample/MapServer',
 *   longitude: -122.4194,
 *   latitude: 37.7749,
 *   view: mapView,
 *   token: 'authentication_token_here',
 *   fallbackLayerName: 'City Boundaries'
 * });
 */
export const identifyMapService = async ({
  url,
  longitude,
  latitude,
  view,
  token,
  fallbackLayerName,
  tolerance,
}: IdentifyMapServiceParams): Promise<HelperFunctionResult | null> => {
  try {
    const identifyParams = new IdentifyParameters({
      tolerance: typeof tolerance === 'number' ? tolerance : 3,
      layerOption: 'top',
      width: view?.width,
      height: view?.height,
      geometry: new Point({
        longitude,
        latitude,
        spatialReference: { wkid: 4326 },
      }),
      mapExtent: view?.extent,
    });

    const authenticatedUrl = token ? `${url}?token=${token}` : url;
    const response = await identify(authenticatedUrl, identifyParams);

    if (response?.results?.length) {
      const sourceTitle =
        response.results[0].layerName ||
        fallbackLayerName ||
        'Unknown Map Service';

      return {
        sourceTitle,
        attributes: {
          ...response.results[0].feature.attributes,
          _sourceLayer: sourceTitle,
        },
      };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Map service identify failed:', error);
  }

  return null;
};
